import { apiService, Role } from "./api";

// Roles service for managing roles cache
export class RolesService {
  private static instance: RolesService;
  private roles: Role[] = [];
  private isLoaded: boolean = false;
  private isLoading: boolean = false;
  private loadPromise: Promise<Role[]> | null = null;

  private constructor() {}

  public static getInstance(): RolesService {
    if (!RolesService.instance) {
      RolesService.instance = new RolesService();
    }
    return RolesService.instance;
  }

  // Get roles with caching
  public async getRoles(): Promise<Role[]> {
    // If already loaded, return cached roles
    if (this.isLoaded) {
      return this.roles;
    }

    // If currently loading, return the existing promise
    if (this.isLoading && this.loadPromise) {
      return await this.loadPromise;
    }

    // Start loading
    this.isLoading = true;
    this.loadPromise = this.loadRoles();

    try {
      const roles = await this.loadPromise;
      return roles;
    } finally {
      this.isLoading = false;
      this.loadPromise = null;
    }
  }

  // Load roles from API
  private async loadRoles(): Promise<Role[]> {
    try {
      const response = await apiService.getRoles();
      if (response.success) {
        this.roles = response.data;
        this.isLoaded = true;
        console.log("Roles loaded and cached:", this.roles);
        return this.roles;
      } else {
        throw new Error("Failed to load roles");
      }
    } catch (error) {
      console.error("Failed to load roles:", error);
      this.isLoaded = false;
      this.roles = [];
      throw error;
    }
  }

  // Get role by ID
  public getRoleById(roleId: string): Role | undefined {
    return this.roles.find((role) => role._id === roleId);
  }

  // Get role by name
  public getRoleByName(roleName: string): Role | undefined {
    return this.roles.find((role) => role.name === roleName);
  }

  // Get display name for role
  public getRoleDisplayName(roleName: string): string {
    const roleMap: { [key: string]: string } = {
      User: "Người dùng",
      Admin: "Quản trị viên",
    };
    return roleMap[roleName] || roleName;
  }

  // Get default role (User)
  public getDefaultRole(): Role | undefined {
    return this.getRoleByName("User");
  }

  // Clear cache (useful for testing or when roles might change)
  public clearCache(): void {
    this.roles = [];
    this.isLoaded = false;
    this.isLoading = false;
    this.loadPromise = null;
    console.log("Roles cache cleared");
  }

  // Check if roles are loaded
  public isRolesLoaded(): boolean {
    return this.isLoaded;
  }

  // Get cached roles (synchronous, returns empty array if not loaded)
  public getCachedRoles(): Role[] {
    return this.roles;
  }
}

// Export singleton instance
export const rolesService = RolesService.getInstance();

// Export types for use in components
export type { Role };
